import { useEffect, useMemo, useState } from "react";
import { KybComplianceStatus } from "../../../features/kyb-compliance/types";
import { getApiErrorMessage } from "../../lib";
import {
  buildKybBusinessesParams,
  getKybBusinesses,
  KybBusinessesResult,
} from "../kyb";

type UseKybBusinessesState = KybBusinessesResult & {
  isLoading: boolean;
  error: string | null;
};

const kybBusinessesCache = new Map<string, KybBusinessesResult>();

export const useKybBusinesses = (params: {
  search?: string;
  sort?: string;
  country?: string;
  status?: KybComplianceStatus;
  businessType?: string;
  businessSource?: string;
  page?: number;
  limit?: number;
}) => {
  const [state, setState] = useState<UseKybBusinessesState>({
    items: [],
    meta: undefined,
    isLoading: true,
    error: null,
  });
  const [refreshCount, setRefreshCount] = useState(0);
  const requestParams = useMemo(
    () =>
      buildKybBusinessesParams({
        search: params.search,
        sort: params.sort,
        country: params.country,
        status: params.status,
        businessType: params.businessType,
        businessSource: params.businessSource,
        page: params.page,
        limit: params.limit,
      }),
    [
      params.businessType,
      params.businessSource,
      params.country,
      params.limit,
      params.page,
      params.search,
      params.sort,
      params.status,
    ],
  );
  const requestKey = JSON.stringify(requestParams);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      const cached = kybBusinessesCache.get(requestKey);
      setState((prev) => ({
        items: cached?.items ?? prev.items,
        meta: cached?.meta ?? prev.meta,
        isLoading: cached ? false : true,
        error: null,
      }));

      try {
        const result = await getKybBusinesses(requestParams);
        if (!isMounted) return;
        kybBusinessesCache.set(requestKey, result);
        setState({
          items: result.items,
          meta: result.meta,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        if (!isMounted) return;
        setState({
          items: [],
          meta: undefined,
          isLoading: false,
          error: getApiErrorMessage(error, "Unable to load businesses"),
        });
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [refreshCount, requestKey, requestParams]);

  return {
    ...state,
    refetch: () => setRefreshCount((prev) => prev + 1),
  };
};
